import { Howl } from 'howler';
import fromPairs from 'lodash/fromPairs';

import alertSoundFile from './sounds/alert.mp3';
import pauseStopSoundFile from './sounds/pausestop.mp3';
import startSoundFile from './sounds/start.mp3';

export type SessionSounds = 'start' | 'pauseStop' | 'alert' | 'disclaimer';

class SoundPlayer<TSoundName extends string> {
  private volume = 0.5;
  private soundSources: Record<TSoundName, string>;
  private soundRecords: Record<TSoundName, Howl> | null = null;
  private isEnabled = true;

  constructor(sounds: Record<TSoundName, string>) {
    this.soundSources = sounds;
  }

  private ensureLoaded(): Record<TSoundName, Howl> {
    if (!this.soundRecords) {
      this.soundRecords = fromPairs(
        Object.entries(this.soundSources).map(([key, value]) => [
          key,
          new Howl({
            src: [value as string],
            volume: this.volume,
          }),
        ]),
      ) as Record<TSoundName, Howl>;
    }
    return this.soundRecords;
  }

  play = (name: TSoundName) => {
    if (this.isEnabled) {
      this.ensureLoaded()[name]?.play();
    }
  };

  setIsEnabled = (isEnabled: boolean) => {
    this.isEnabled = isEnabled;
  };

  /** Note: calling `on()` triggers Howl initialization. Call after first `play()` to avoid AudioContext warnings. */
  on = (name: TSoundName, event: string, callback: () => void) => {
    this.ensureLoaded()[name]?.on(event, callback);
  };
}

export const sessionSoundPlayer = new SoundPlayer<SessionSounds>({
  // https://www.zapsplat.com/music/high-tech-beep-with-reverb-5/
  start: startSoundFile,
  // https://www.zapsplat.com/music/high-tech-cyber-beep-7/
  pauseStop: pauseStopSoundFile,
  // https://www.zapsplat.com/music/alert-chime-short-musical-notification-error-1/
  alert: alertSoundFile,

  disclaimer: alertSoundFile,
});

export type SessionSoundPlayer = typeof sessionSoundPlayer;
